/**
 * Read-only policy matrix. Renders parsed policy_terms / policy_facts only.
 * No parser calls. Hide when nothing is on the payload.
 */
import React from 'react';
import FinancialValue from '../shared/FinancialValue';
import { PolicyFacts } from './pickComputedSurfaces';

type Row = { label: string; value: React.ReactNode };

function asNumber(v: unknown): number | undefined {
  if (v === undefined || v === null || v === '') return undefined;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function text(v: unknown): string | null {
  if (v === undefined || v === null || v === '') return null;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

export default function PolicyMatrixCard({ facts }: { facts: PolicyFacts | null }) {
  if (!facts) return null;
  const rows: Row[] = [
    { label: 'Form', value: text(facts.form) },
    { label: 'BI definition', value: text(facts.bi_definition) },
    { label: 'Extra expense', value: text(facts.extra_expense) },
    { label: 'Period of restoration', value: text(facts.period_of_restoration) },
    { label: 'Waiting period (hours)', value: text(facts.waiting_period_hours) },
    { label: 'Waiting period retroactive', value: text(facts.waiting_period_retroactive) },
    { label: 'Coinsurance %', value: text(facts.coinsurance_pct) },
    {
      label: 'Actual coverage limit',
      value: <FinancialValue value={asNumber(facts.actual_coverage_limit)} />,
    },
    {
      label: 'Annual BI value',
      value: <FinancialValue value={asNumber(facts.annual_bi_value)} />,
    },
    {
      label: 'Policy limit',
      value: <FinancialValue value={asNumber(facts.policy_limit)} />,
    },
    {
      label: 'Dollar deductible',
      value: <FinancialValue value={asNumber(facts.dollar_deductible)} />,
    },
    { label: 'ADV deductible days', value: text(facts.adv_deductible_days) },
    { label: 'EPI included', value: text(facts.epi_included) },
    { label: 'Civil authority', value: text(facts.civil_authority) },
    { label: 'Service interruption', value: text(facts.service_interruption) },
    { label: 'Ordinance or law', value: text(facts.ordinance_or_law) },
  ].filter((r) => r.value !== null && r.value !== undefined);

  if (!rows.length) return null;

  return (
    <section className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] mb-6">
      <header className="px-5 h-12 flex items-center">
        <h2 className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--color-text-muted)]">
          Policy matrix
        </h2>
      </header>
      <dl className="border-t border-[var(--color-border)]">
        {rows.map((r) => (
          <div
            key={r.label}
            className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-6 px-5 min-h-11 items-baseline border-t border-[var(--color-hairline)]"
          >
            <dt className="text-[13px] text-[var(--color-text-secondary)] py-2">{r.label}</dt>
            <dd className="text-[13px] text-[var(--color-text-primary)] py-2 text-right">{r.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
